import { logReferUserAction } from '@office-start/telemetry-service';

import { InstrumentationService, KnownAreas, KnownInstrumentationEvents, KnownReferralPlatform } from './instrumentation.service';

export class ClickLogging {
    private static readonly supportedClickTargets: any = {
        'A': true,
        'IMG': true,
        'AREA': true,
        'INPUT': true,
        'BUTTON': true
    };

    public static setup(instrumentationService: InstrumentationService): void {
        if (document.body && document.body.addEventListener) {
            const eventName = navigator.appVersion.toUpperCase().indexOf('MSIE') !== -1 ? 'click' : 'mousedown';
            document.body.addEventListener(eventName, function (event: MouseEvent) {
                ClickLogging.processClick(event, instrumentationService);
            });
        }
    }

    private static processClick(
        event: MouseEvent,
        instrumentationService: InstrumentationService
    ): void {
        // Determine element that acts as click target
        let clickTarget: any = event.srcElement || event.target;
        while (clickTarget) {
            if (clickTarget.tagName && ClickLogging.supportedClickTargets.hasOwnProperty(clickTarget.tagName.toUpperCase())) {
                break;
            }

            clickTarget = clickTarget.parentElement || clickTarget.parentNode;
        }

        // Abort if clicked element does not ladder up to supported click target types;
        // e.g. when user clicks on whitespace around elements
        if (!clickTarget) {
            return;
        }

        // Skip elements that do custom click logging; do that to avoid double logging
        const clicklogSetting = clickTarget.getAttribute('data-clicklog');
        if (clicklogSetting && clicklogSetting.toUpperCase() === 'CUSTOM') {
            return;
        }

        const clickTargetId: string = clickTarget.id || 'Unknown';

        let referral = clickTarget.getAttribute('data-referral');
        if (typeof referral === 'undefined' || referral === null || referral.length === 0) {
            referral = ClickLogging.referralFromElement(clickTarget);
        }

        const referralPlatform = clickTarget.getAttribute('data-referral-platform');

        let area = clickTarget.getAttribute('data-area');
        if (typeof area === 'undefined' || area === null || area.length === 0) {
            area = ClickLogging.areaFromElement(clickTarget);
        }

        const isPageNav = !!clickTarget.getAttribute('data-nav');

        ClickLogging.logGenericClick(clickTargetId, area, referral, referralPlatform, isPageNav, instrumentationService);
        //TODO: Remove abovoe aria logging after OTEL migration
        logReferUserAction(clickTargetId, area ?? 'Other', referral);
    }

    private static logGenericClick(
        clickTargetId: string,
        clickArea: string,
        clickReferral: string,
        referralPlatform: KnownReferralPlatform,
        isPageNav: boolean,
        instrumentationService: InstrumentationService): void {

        instrumentationService
            .logClickEvent(KnownInstrumentationEvents.GenericClick, clickTargetId, clickArea, clickReferral, referralPlatform, undefined, undefined, undefined, isPageNav);
    }

    private static areaFromElement(element: any): string {
        const elementId: string = element.id || null;
        if (elementId && elementId.length) {
            const shellElementIdPrefix = 'O365_';
            if (elementId.lastIndexOf(shellElementIdPrefix, shellElementIdPrefix.length) === 0) {
                return KnownAreas.Shell;
            }

            const feedbackElementPrefix = 'obf-';
            if (elementId.lastIndexOf(feedbackElementPrefix, feedbackElementPrefix.length) === 0) {
                return KnownAreas.Feedback;
            }
        }

        const elementClassList: DOMTokenList = element.classList || null;
        if (elementClassList && elementClassList.length) {
            const shellElementClassPrefix = 'o365cs-';
            for (let classItemIndex = 0; classItemIndex < elementClassList.length; classItemIndex++) {
                const classItem = elementClassList.item(classItemIndex);
                if (classItem.lastIndexOf(shellElementClassPrefix, shellElementClassPrefix.length) === 0) {
                    return KnownAreas.Shell;
                }
            }
        }

        // TODO: Should we add support for identifying generic clicks coming from UX embedded from external sources (such as PeopleCard)?
        return null;
    }

    private static referralFromElement(element: any): string {
        // IMPORTANT: Shell uses inconsistent id naming convention where app tiles in main view have id with prefix and
        // app tiles in All apps list have id that corresponds to canonical referral name without any prefix. To account
        // for that we first analyze element id and if that does not yield any result then check for presence of class.
        const elementId: string = element.id || null;
        if (elementId && elementId.length) {
            const shellAppTileElementIdPrefix = 'O365_AppTile_';
            if (elementId.lastIndexOf(shellAppTileElementIdPrefix, shellAppTileElementIdPrefix.length) === 0) {
                let referral = elementId.substring(shellAppTileElementIdPrefix.length);

                // IMPORTANT: there appear to be different naming convention used for app tile ids inside Consumer and
                // Commercial shells. App tile id in Consumer shell is prepended with Shell prefix
                const shellReferralPrefix = 'Shell';
                if (referral.lastIndexOf(shellReferralPrefix, shellReferralPrefix.length) === 0) {
                    referral = referral.substring(shellReferralPrefix.length);
                }

                return referral;
            }

            const elementClassList: DOMTokenList = element.classList || null;
            if (elementClassList && elementClassList.length) {
                const shellAppTileElementClass = 'o365cs-nav-appTile';
                if (elementClassList.contains(shellAppTileElementClass)) {
                    return elementId;
                }
            }
        }

        // TODO: Should we add support for identifying referrals coming from UX embedded from external sources (such as PeopleCard)?
        return null;
    }
}
